import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import config from "../../Config";
import TransportMenu from "./TransportMenu";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { scrollToTop } from "../Shared/ScrollOnTop";

/*
 * Fuel and maintenance log - the screen used every day.
 *
 * The meter reading is the important field. The system this replaces asked for a typed
 * "KM Covered" and got 0 on 58 of its 71 rows, so Rs 2.1m of fuel produced no mileage
 * figure at all. Here the operator enters the odometer, distance is derived from the
 * previous reading, and km/litre falls out on its own.
 */
function VehicleLog() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [vehicles, setVehicles] = useState([]);

  const [filterVehicle, setFilterVehicle] = useState("");
  const [filterMonth, setFilterMonth] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validated, setValidated] = useState(false);
  const [previous, setPrevious] = useState(null);

  const blank = {
    ID: 0,
    Vehicle_ID: "",
    Log_Date: moment().format("YYYY-MM-DD"),
    Odometer_Reading: "",
    Fuel_Quantity: "",
    Fuel_Rate: "",
    Fuel_Cost: "",
    Maintenance_Type: "",
    Maintenance_Cost: "",
    Vendor: "",
    Invoice_No: "",
    Remarks: "",
  };
  const [entry, setEntry] = useState(blank);

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      SchoolID: 1,
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType]
  );

  const post = useCallback(
    (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      }),
    []
  );

  const monthRange = useCallback(() => {
    if (!filterMonth) return { From_Date: null, To_Date: null };
    return {
      From_Date: moment(filterMonth, "YYYY-MM").startOf("month").format("YYYY-MM-DD"),
      To_Date: moment(filterMonth, "YYYY-MM").endOf("month").format("YYYY-MM-DD"),
    };
  }, [filterMonth]);

  const loadLogs = useCallback(() => {
    setLoading(true);
    post(
      "VehicleLog/GetLogs",
      auth({
        Vehicle_ID: filterVehicle ? parseInt(filterVehicle, 10) : 0,
        ...monthRange(),
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          setLogs(res.data.ListofRecords || []);
          setSummary(res.data.Summary || null);
        } else {
          setLogs([]);
          setSummary(null);
          toast.error(res.data.status_message || "Could not load entries", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      )
      .finally(() => setLoading(false));
  }, [post, auth, filterVehicle, monthRange]);

  useEffect(() => {
    document.title = "Fuel & Maintenance Log";
    scrollToTop();
    post("Vehicle/GetVehicles", auth({ Is_Active: true }))
      .then((res) => {
        if (res.data.status_code === 1) setVehicles(res.data.ListofRecords || []);
      })
      .catch(() => setVehicles([]));
  }, [post, auth]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  /* Fetch the last reading so the operator can see what the new one has to beat. */
  const loadPrevious = (vehicleId, onDate, excludeId) => {
    if (!vehicleId) {
      setPrevious(null);
      return;
    }
    post(
      "VehicleLog/GetPreviousEntry",
      auth({ Vehicle_ID: parseInt(vehicleId, 10), Log_Date: onDate, ID: excludeId || 0 })
    )
      .then((res) => setPrevious(res.data.status_code === 1 ? res.data.Previous : null))
      .catch(() => setPrevious(null));
  };

  const openAdd = () => {
    setEntry(blank);
    setPrevious(null);
    setValidated(false);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setEntry({
      ID: row.ID,
      Vehicle_ID: String(row.Vehicle_ID),
      Log_Date: moment(row.Log_Date).format("YYYY-MM-DD"),
      Odometer_Reading: row.Odometer_Reading ?? "",
      Fuel_Quantity: row.Fuel_Quantity ?? "",
      Fuel_Rate: row.Fuel_Rate ?? "",
      Fuel_Cost: row.Fuel_Cost ?? "",
      Maintenance_Type: row.Maintenance_Type || "",
      Maintenance_Cost: row.Maintenance_Cost ?? "",
      Vendor: row.Vendor || "",
      Invoice_No: row.Invoice_No || "",
      Remarks: row.Remarks || "",
    });
    setValidated(false);
    setShowForm(true);
    loadPrevious(row.Vehicle_ID, moment(row.Log_Date).format("YYYY-MM-DD"), row.ID);
  };

  const set = (field, value) => {
    setEntry((prev) => {
      const next = { ...prev, [field]: value };

      // Quantity x rate = cost. Fill in whichever the operator left blank rather than
      // making them work it out, and never overwrite something typed deliberately.
      const qty = parseFloat(field === "Fuel_Quantity" ? value : next.Fuel_Quantity);
      const rate = parseFloat(field === "Fuel_Rate" ? value : next.Fuel_Rate);
      const cost = parseFloat(field === "Fuel_Cost" ? value : next.Fuel_Cost);

      if (field === "Fuel_Quantity" || field === "Fuel_Rate") {
        if (qty > 0 && rate > 0) next.Fuel_Cost = String(Math.round(qty * rate));
      } else if (field === "Fuel_Cost") {
        if (qty > 0 && cost > 0) next.Fuel_Rate = String((cost / qty).toFixed(2));
      }
      return next;
    });

    if (field === "Vehicle_ID" || field === "Log_Date") {
      const vid = field === "Vehicle_ID" ? value : entry.Vehicle_ID;
      const dt = field === "Log_Date" ? value : entry.Log_Date;
      loadPrevious(vid, dt, entry.ID);
    }
  };

  const num = (v) => (v === "" || v === null || v === undefined ? 0 : parseFloat(v));

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setSaving(true);
    post(
      "VehicleLog/SaveLog",
      auth({
        ID: entry.ID,
        Vehicle_ID: parseInt(entry.Vehicle_ID, 10),
        Log_Date: entry.Log_Date,
        Odometer_Reading: entry.Odometer_Reading === "" ? null : num(entry.Odometer_Reading),
        Fuel_Quantity: num(entry.Fuel_Quantity),
        Fuel_Rate: entry.Fuel_Rate === "" ? null : num(entry.Fuel_Rate),
        Fuel_Cost: num(entry.Fuel_Cost),
        Maintenance_Type: entry.Maintenance_Type,
        Maintenance_Cost: num(entry.Maintenance_Cost),
        Vendor: entry.Vendor,
        Invoice_No: entry.Invoice_No,
        Remarks: entry.Remarks,
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          setShowForm(false);
          loadLogs();
        } else {
          // Odometer going backwards and empty entries land here.
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 8000,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      )
      .finally(() => setSaving(false));
  };

  const deleteLog = (row) => {
    if (
      !window.confirm(
        `Delete the ${moment(row.Log_Date).format("DD-MMM-YYYY")} entry for ${row.Registration_No}?`
      )
    ) {
      return;
    }
    post("VehicleLog/DeleteLog", auth({ ID: row.ID }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          loadLogs();
        } else {
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      );
  };

  const money = (n) =>
    n === null || n === undefined
      ? "—"
      : Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const renderRows = () => {
    if (logs.length === 0) {
      return (
        <tr>
          <td colSpan="11" className="text-center text-muted py-4">
            No entries for this selection
          </td>
        </tr>
      );
    }

    return logs.map((row, i) => (
      <tr key={row.ID}>
        <td>{i + 1}</td>
        <td className="text-nowrap">{moment(row.Log_Date).format("DD-MMM-YYYY")}</td>
        <td className="fw-bold">{row.Registration_No}</td>
        <td className="text-end">{row.Fuel_Quantity > 0 ? row.Fuel_Quantity : "—"}</td>
        <td className="text-end">{row.Fuel_Rate ? Number(row.Fuel_Rate).toFixed(2) : "—"}</td>
        <td className="text-end">
          {row.Odometer_Reading ? money(row.Odometer_Reading) : (
            <span className="text-muted" title="No meter reading recorded, so mileage cannot be worked out">—</span>
          )}
        </td>
        <td className="text-end">{row.Distance_KM ? money(row.Distance_KM) : "—"}</td>
        <td className="text-end">
          {row.KmPerLitre ? (
            <span className="badge bg-info text-dark">{row.KmPerLitre}</span>
          ) : (
            "—"
          )}
        </td>
        <td>{row.Maintenance_Type || "—"}</td>
        <td className="text-end">
          {money(row.Fuel_Cost)}
          {row.Maintenance_Cost > 0 ? (
            <div className="small text-muted">+ {money(row.Maintenance_Cost)} maint</div>
          ) : null}
        </td>
        <td>
          <ul className="inline-action" aria-labelledby="dropdownMenuButton1">
            <li>
              <button
                type="button"
                className="green"
                title="Edit entry"
                onClick={() => openEdit(row)}
              >
                <i className="ri-edit-line"></i>
              </button>
            </li>
            <li>
              <button
                type="button"
                className="red"
                title="Delete entry"
                onClick={() => deleteLog(row)}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </li>
          </ul>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <TransportMenu />
      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Transport/TransportDashboard">Transport</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Fuel &amp; Maintenance
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Fuel &amp; Maintenance Log</div>
                <p className="text-muted small mb-0">
                  Record the odometer at every filling — distance and km/litre are worked
                  out from it, so mileage needs no separate entry.
                </p>
              </div>
              <div className="col-md-6">
                <div className="page-right-actions">
                  <a
                    href={`/Transport/PrintVehicleLog?vehicle=${filterVehicle}&month=${filterMonth}`}
                    target="_blank"
                    rel="noreferrer"
                    className="button button-white button-icon"
                  >
                    Print
                    <i className="ri-printer-line"></i>
                  </a>
                  <button
                    type="button"
                    onClick={openAdd}
                    className="button button-white button-icon"
                  >
                    Add Entry
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Entries</div>
                    <div className="h4 mb-0">{summary ? summary.Entries : 0}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Fuel cost</div>
                    <div className="h4 mb-0">
                      Rs. {summary ? money(summary.FuelCost) : 0}
                    </div>
                    <div className="small text-muted">
                      {summary ? money(summary.FuelQuantity) : 0} litres
                      {summary && summary.AvgRate
                        ? ` @ ${Number(summary.AvgRate).toFixed(2)}`
                        : ""}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Maintenance</div>
                    <div className="h4 mb-0">
                      Rs. {summary ? money(summary.MaintenanceCost) : 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Total running cost</div>
                    <div className="h4 mb-0 text-danger">
                      Rs. {summary ? money(summary.TotalCost) : 0}
                    </div>
                    <div className="small text-muted">
                      {summary && summary.AvgKmPerLitre
                        ? `${summary.AvgKmPerLitre} km/litre`
                        : "mileage needs meter readings"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="row g-2 mb-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Vehicle</label>
                    <select
                      className="form-select"
                      value={filterVehicle}
                      onChange={(e) => setFilterVehicle(e.target.value)}
                    >
                      <option value="">All vehicles</option>
                      {vehicles.map((v) => (
                        <option key={v.Vehicle_ID} value={v.Vehicle_ID}>
                          {v.Registration_No}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small mb-1">Month</label>
                    <input
                      type="month"
                      className="form-control"
                      value={filterMonth}
                      onChange={(e) => setFilterMonth(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <button
                      type="button"
                      className="button button-light"
                      onClick={() => {
                        setFilterVehicle("");
                        setFilterMonth("");
                      }}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-theme">
                    <thead>
                      <tr>
                        <th>Sr.</th>
                        <th>Date</th>
                        <th>Vehicle</th>
                        <th className="text-end">Litres</th>
                        <th className="text-end">Rate</th>
                        <th className="text-end">Odometer</th>
                        <th className="text-end">Distance</th>
                        <th className="text-end">Km/L</th>
                        <th>Maintenance</th>
                        <th className="text-end">Cost</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="11" className="text-center py-4">
                            <Spinner animation="border" role="status" />
                          </td>
                        </tr>
                      ) : (
                        renderRows()
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* add / edit */}
      <Modal
        className="modal-theme"
        show={showForm}
        onHide={() => setShowForm(false)}
        size="lg"
        centered
      >
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "18px" }}>
              {entry.ID ? "Edit entry" : "Add fuel / maintenance entry"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">
                  Vehicle <span className="text-danger">*</span>
                </label>
                <Form.Select
                  required
                  value={entry.Vehicle_ID}
                  onChange={(e) => set("Vehicle_ID", e.target.value)}
                >
                  <option value="">-- select --</option>
                  {vehicles.map((v) => (
                    <option key={v.Vehicle_ID} value={v.Vehicle_ID}>
                      {v.Registration_No} {v.Model ? `(${v.Model})` : ""}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Select a vehicle.
                </Form.Control.Feedback>
              </div>
              <div className="col-md-6">
                <label className="form-label">
                  Date <span className="text-danger">*</span>
                </label>
                <Form.Control
                  type="date"
                  required
                  max={moment().format("YYYY-MM-DD")}
                  value={entry.Log_Date}
                  onChange={(e) => set("Log_Date", e.target.value)}
                />
              </div>

              {entry.Vehicle_ID && !previous ? (
                <div className="col-12">
                  <div className="border rounded bg-light text-dark small mb-0 px-3 py-2">
                    No meter reading has been logged for this bus yet. Enter one and
                    mileage starts from the next filling.
                  </div>
                </div>
              ) : null}

              {previous ? (
                <div className="col-12">
                  <div className="border rounded bg-light text-dark small mb-0 px-3 py-2">
                    Last recorded meter reading:{" "}
                    <b>{money(previous.Odometer_Reading)}</b> on{" "}
                    <b>{moment(previous.Log_Date).format("DD-MMM-YYYY")}</b>. The new
                    reading must be higher; the difference becomes the distance.
                  </div>
                </div>
              ) : null}

              <div className="col-md-4">
                <label className="form-label">Odometer reading</label>
                <Form.Control
                  type="number"
                  step="1"
                  min={0}
                  placeholder="meter at this filling"
                  value={entry.Odometer_Reading}
                  onChange={(e) => set("Odometer_Reading", e.target.value)}
                />
                <div className="form-text">Distance is derived from this.</div>
              </div>
              <div className="col-md-4">
                <label className="form-label">Fuel quantity (litres)</label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={entry.Fuel_Quantity}
                  onChange={(e) => set("Fuel_Quantity", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Rate per litre</label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min={0}
                  value={entry.Fuel_Rate}
                  onChange={(e) => set("Fuel_Rate", e.target.value)}
                />
                <div className="form-text">Pump price on the day.</div>
              </div>

              <div className="col-md-4">
                <label className="form-label">Fuel cost</label>
                <Form.Control
                  type="number"
                  step="1"
                  min={0}
                  value={entry.Fuel_Cost}
                  onChange={(e) => set("Fuel_Cost", e.target.value)}
                />
                <div className="form-text">Quantity × rate, editable.</div>
              </div>
              <div className="col-md-8">
                <label className="form-label">Maintenance carried out</label>
                <Form.Control
                  type="text"
                  maxLength={300}
                  placeholder="e.g. Engine Oil 14lt, oil filter"
                  value={entry.Maintenance_Type}
                  onChange={(e) => set("Maintenance_Type", e.target.value)}
                />
              </div>

              <div className="col-md-4">
                <label className="form-label">Maintenance cost</label>
                <Form.Control
                  type="number"
                  step="1"
                  min={0}
                  value={entry.Maintenance_Cost}
                  onChange={(e) => set("Maintenance_Cost", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Vendor</label>
                <Form.Control
                  type="text"
                  maxLength={150}
                  value={entry.Vendor}
                  onChange={(e) => set("Vendor", e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Invoice no.</label>
                <Form.Control
                  type="text"
                  maxLength={50}
                  value={entry.Invoice_No}
                  onChange={(e) => set("Invoice_No", e.target.value)}
                />
              </div>

              <div className="col-12">
                <label className="form-label">Remarks</label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={500}
                  value={entry.Remarks}
                  onChange={(e) => set("Remarks", e.target.value)}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="button button-light"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
            <button type="submit" className="button button-primary" disabled={saving}>
              {saving ? "Saving…" : entry.ID ? "Update Entry" : "Add Entry"}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}

export default VehicleLog;
